import { Button, Group, List, Modal, Stack, Text } from "@mantine/core";
import { type JSX, useEffect } from "react";

import { useAnalytics } from "@/analytics";
import { env } from "@/config";
import { useLogger } from "@/sdk/logging/LoggerProvider";

import { useTroubleshootingStore } from "./troubleshoot.store";

/** EVL/audio troubleshooting shown in the voice build when a call fails to connect. */
const EVL_GUIDANCE = {
    title: "Call failed to connect-refresh EVL and check your audio settings",
    steps: [
        "Go to EVL and refresh it (or load it if not already open)",
        "Make sure EVL is fully connected",
        "Go to Available",
    ],
};

/** Routing-profile guidance shown in the chat build (a voice contact reached a chat expert). */
const CHANNEL_MISMATCH_GUIDANCE = {
    title: "Missed a voice contact while in chat mode",
    steps: [
        "Your routing profile may still be set to voice",
        "Ask your supervisor to switch you to the chat profile",
        "Then set yourself to Available",
    ],
};

export function Troubleshooting(): JSX.Element | null {
    const logger = useLogger({ module: "Troubleshooting" });
    const { setIsTroubleshooting } = useTroubleshootingStore();
    const { dispatcher } = useAnalytics();

    // In the chat build a "technical issues" event can only come from a mis-routed voice
    // contact, i.e. the routing profile is still voice-only - not an EVL/audio failure.
    const isChatBuild = env.channel === "chat";
    const guidance = isChatBuild ? CHANNEL_MISMATCH_GUIDANCE : EVL_GUIDANCE;

    const handleCloseTroubleshooting = () => {
        setIsTroubleshooting(false);
    };

    useEffect(() => {
        logger.info("Troubleshooting | open");
        if (isChatBuild) {
            void dispatcher.dispatchBusinessEvent("ChannelMismatchDetected", { appChannel: env.channel });
        } else {
            void dispatcher.dispatchBusinessEvent("TroubleshootingOpen");
        }
    }, [dispatcher, logger, isChatBuild]);

    const onClose = () => {
        // Keep open/close events paired per channel so analytics funnels don't undercount.
        void dispatcher.dispatchBusinessEvent(isChatBuild ? "ChannelMismatchClose" : "TroubleshootingClose");
        logger.info("Troubleshooting | close");
        setIsTroubleshooting(false);
    };

    return (
        <Modal
            opened
            centered
            onClose={onClose}
            withCloseButton={false}
            closeOnClickOutside={false}
            closeOnEscape={false}
        >
            <Stack>
                <Text size="lg" c="gray.3">
                    {guidance.title}
                </Text>
                <List mt="sm" size="sm" c="gray.6">
                    {guidance.steps.map((step) => (
                        <List.Item key={step}>{step}</List.Item>
                    ))}
                </List>
                <Group justify="right">
                    <Button onClick={handleCloseTroubleshooting}>Ok</Button>
                </Group>
            </Stack>
        </Modal>
    );
}
